import { FC, useState } from "react";
import { Asset, Entry } from "contentful";
import useStorefrontApi from "../../hooks/useStorefrontApi";
import {
  Badge,
  ContentContainer,
  TextBadgeWrapper,
  HoverImage,
  ImageContainer,
  Price,
  Tile,
  Title,
} from "./styles";
import ProductTileSwatches, { SwatchVariant } from "./ProductTileSwatches";
import FenderProductTile, { TileVariant } from "../FenderProductTile";

interface ProductTileProps extends Entry {
  id: string,
  layout: string,
  brand: string,
  parentProps: any,
  fields: {
    sku: string;
    imageAsset?: Asset;
    hoverAsset?: Asset;
  }
}

const getProductQuery = (sku: string) => `
  query {
    products(first: 1, query: "${sku}") {
      nodes {
        id
        title
        handle
        productType
        tags
        collections(first: 10) {
          nodes {
            id
            handle
            title
          }
        }
        variantColorsList: metafield(namespace: "custom", key: "variant_colors_list") {
          value
        }
        images(first: 2) {
          nodes {
            url
          }
        }
        priceRange {
          minVariantPrice {
            amount
          }
          maxVariantPrice {
            amount
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
          }
          maxVariantPrice {
            amount
          }
        }
        availableForSale
        variants(first: 20) {
          nodes {
            id
            sku
            image {
              url
            }
            price {
              amount
            }
            calloutMessage: metafield(namespace: "custom", key: "callout_message") {
              value
            }
            newVariant: metafield(namespace: "custom", key: "new_variant") {
              value
            }
            productBadgeOverride: metafield(namespace: "custom", key: "product_badge_override") {
              value
            }
            productBadge: metafield(namespace: "custom", key: "product_badge") {
              value
            }
            textBadge: metafield(namespace: "custom", key: "text_badge") {
              value
            }
            textBadge2: metafield(namespace: "custom", key: "text_badge_2") {
              value
            }
            variantMedia: metafield(namespace: "custom", key: "variant_media") {
              value
            }
          }
        }
        textBadge: metafield(namespace: "custom", key: "text_badge") {
          value
        }
        textBadgeTwo: metafield(namespace: "custom", key: "text_badge_two") {
          value
        }
        orientation: metafield(namespace: "PIM", key: "orientation") {
          value
        }
        calloutMessage: metafield(namespace: "custom", key: "callout_message") {
          value
        }
        productBadge: metafield(namespace: "custom", key: "product_badge") {
          value
        }
      }
    }
  }
`;

const parseVariantMedia = (metafieldValue: string) => {
  const parsed = JSON.parse(metafieldValue);
  const { variantMedia } = parsed;

  if (variantMedia?.images?.length > 1) {
    return variantMedia.images[1].url;
  }

  return '';
}

interface ShopifyVariant {
  id: string;
  sku: string;
  image: { url: string; };
  price: { amount: string; };
  calloutMessage?: { value: string; };
  newVariant?: { value: boolean; };
  productBadgeOverride?: { value: boolean; };
  productBadge?: { value: string; };
  textBadge?: { value: string; };
  textBadge2?: { value: string; };
  variantMedia?: { value: string; };
}

const buildFenderTileVariants = (variants = []): TileVariant[] => {
  const parsedVariants: TileVariant[] = [];

  variants.forEach((variant: ShopifyVariant) => {
    const {
      calloutMessage,
      newVariant,
      productBadgeOverride,
      productBadge,
      textBadge,
      textBadge2,
      variantMedia,
      price
    } = variant;

    let pillBadgeText = '';
    if (productBadgeOverride?.value && productBadge?.value) {
      pillBadgeText = productBadge.value;
    }

    let textBadges = [];
    if (textBadge?.value) {
      textBadges.push(textBadge.value);
    }
    if (textBadge2?.value) {
      textBadges.push(textBadge2.value);
    }

    let imageUrls = [];
    let swatchUrl = '';
    let swatchName = '';
    if (variantMedia?.value) {
      const mediaObject = JSON.parse(variantMedia?.value);
      if (mediaObject && mediaObject.variantMedia) {
        const { images, swatch } = mediaObject.variantMedia;
        // Image 1 should be the default variant image, 2 should be the back facing for hover.
        if (images && images[0]?.url) {
          imageUrls.push(images[0].url);
        }
        if (images && images[1]?.url) {
          imageUrls.push(images[1].url);
        }

        // The color swatch should be the first image in this array.
        if (swatch && swatch[0]?.url) {
          swatchUrl = swatch[0].url;
        }
        if (swatch && swatch[0]?.altText) {
          swatchName = swatch[0].altText;
        }
      }
    }  

    parsedVariants.push({
      outOfStockFlag: false,
      backInStockFlag: false,
      inventory: -1,
      // Todo: these top 3 need real values from the Shopify api
      calloutMessage: calloutMessage?.value || '',
      imageUrls,
      isNewVariant: newVariant?.value || false,
      pillBadgeOverride: pillBadgeText,
      price: price.amount,
      swatchName,
      swatchUrl,
      textBadges,
    });
  });

  return parsedVariants;
}

const ProductTile: FC<ProductTileProps> = ({
  id,
  layout,
  brand,
  parentProps,
  fields: { sku, imageAsset, hoverAsset },
}) => {
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  const theme = parentProps?.theme;

  if (!sku) {
    return null;
  }

  const query = getProductQuery(sku);
  const { storefrontData } = useStorefrontApi(query);

  // @ts-ignore
  if (!storefrontData || !storefrontData.products || storefrontData.products.nodes.length === 0) {
    return null;
  }

  // @ts-ignore
  const {
    title,
    handle,
    images,
    orientation,
    priceRange,
    compareAtPriceRange,
    variants,
    productType,
    variantColorsList,
    tags,
    collections,
    availableForSale,
    textBadge,
    textBadgeTwo,
    productBadge,
    calloutMessage,
    id: productId
    // @ts-ignore
  } = storefrontData.products.nodes[0];

  const leftHanded = orientation?.value == 'Left-Hand';

  let productHasTargetVariant = false;
  // @ts-ignore
  variants.nodes.forEach(variant => {
    if (variant.sku == sku) {
      productHasTargetVariant = true;
    }
  });

  if (!productHasTargetVariant) {
    return null;
  }

  const marketMetafieldsEl = document.getElementById('react-market-metafields');
  const disablePricing = (marketMetafieldsEl?.dataset?.disablePricing == 'true');
  const shopCurrencySymbol = document.getElementById('shop-currency-symbol')?.dataset?.currencySymbol || '$';
  const price = priceRange.minVariantPrice.amount;
  const compareAtPrice = compareAtPriceRange?.minVariantPrice?.amount || null;
  const collectionHandles = collections.nodes.map((collection: any) => collection.handle).join(',');
  const productTags = tags.join(',');
  const priceVaries = priceRange.minVariantPrice.amount !== priceRange.maxVariantPrice.amount;
  const compareAtPriceVaries = compareAtPriceRange.minVariantPrice.amount !== compareAtPriceRange.maxVariantPrice.amount
  const collectionIds = collections.nodes.map((collection: any) => collection.id.replace('gid://shopify/Collection/', '')).join(',');

  let imageUrl = '';
  let hoverUrl = '';
  let isCustomHoverImage = false;

  // Prefer Contentful asset field over native Shopify.
  if (imageAsset && imageAsset.fields.file) {
    imageUrl = `https:${imageAsset.fields.file.url}`;
  } else {
    // @ts-ignore
    variants.nodes.forEach(variant => {
      if (variant.sku == sku) {
        productHasTargetVariant = true;
        imageUrl = variant.image?.url;
      }
    });

    // Use the parent product image if the variant did not have one.
    if (!imageUrl && images.nodes.length > 0) {
      imageUrl = images.nodes[0].url;
    }
  }

  // Prefer Contentful asset field over native Shopify.
  let hoverObjectFit = 'contain';
  if (hoverAsset && hoverAsset.fields.file) {
    hoverUrl = `https:${hoverAsset.fields.file.url}`;
    hoverObjectFit = 'cover';
    isCustomHoverImage = true;
  } else {
    // @ts-ignore
    variants.nodes.forEach(variant => {
      if (variant.sku == sku && variant?.variantMedia?.value) {
        const variantHoverUrl = parseVariantMedia(variant.variantMedia.value);
        if (variantHoverUrl) {
          hoverUrl = variantHoverUrl;
        }
      }
    });

    // Use the parent hover image if the variant did not have one.
    if (!hoverUrl && images.nodes.length > 1) {
      hoverUrl = images.nodes[1].url;
    }
  }

  const swatches: SwatchVariant[] = [];
  if (variantColorsList) {
    const colorPairs = variantColorsList.value.split(',');
    colorPairs.forEach((colorPair: string, index: number) => {
      const [swatchName, swatchImageUrl] = colorPair.split('|');
      const variantImageUrl = variants.nodes[index]?.image?.url;
      swatches.push({
        swatchName,
        swatchImageUrl,
        variantImageUrl: variantImageUrl || swatchImageUrl,
        productUrl: `/products/${handle}`,
      });
    });
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    setActiveVariantIndex(0);
  }

  const baseImageUrl = swatches[0]?.variantImageUrl || imageUrl;
  const baseNoRotate = (brand == 'presonus' || baseImageUrl?.includes("nr.png"));

  if (brand == 'fender') {
    const fenderTileVariants = buildFenderTileVariants(variants?.nodes);
    let dataCategory = '';
    if (collections && collections.nodes.length > 0) {
      dataCategory = collections.nodes[0].title;
    }

    return (
      <FenderProductTile 
        dataCategory={dataCategory}
        layout="grid"
        locale="en-US"
        productHandle={handle}
        productId={productId}
        productName={title}
        productPrice={price}
        productSku={sku}
        productTags={tags}
        variants={fenderTileVariants}
      />
    );
  }

  return (
    <Tile
      data-category={productType}
      data-product-name={title}
      data-sku={sku}
      data-recommendation-clicked
      href={`/products/${handle}`}
      data-component-id={id}
      $brand={brand}
      $theme={theme}
      $hasHoverImg={!!hoverUrl}
      $showVariantHoverImage={(activeVariantIndex > 0)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      data-la-dn-product-handle={handle}
      data-la-dn-product-id={productId.replace('gid://shopify/Product/', '')}
      data-la-dn-product-collection-handles={collectionHandles}
      data-la-dn-product-collection-ids={collectionIds}
      data-la-dn-product-available={availableForSale}
      data-la-dn-product-tags={productTags}
      data-la-dn-product-price-varies={priceVaries}
      data-la-dn-product-compare-at-price-varies={compareAtPriceVaries}
      // Discount Ninja expects prices in cents.
      data-la-dn-product-price={price * 100}
      data-la-dn-product-price-min={priceRange.minVariantPrice.amount * 100}
      data-la-dn-product-compare-at-price={compareAtPrice * 100}
    >
      <ImageContainer $layout={layout} $noRotate={baseNoRotate} $leftHanded={leftHanded} $brand={brand}>
        <img src={baseImageUrl} alt={title} />
        {hoverUrl && (
          <HoverImage
            src={hoverUrl}
            $objectFit={hoverObjectFit}
            $isCustomHoverImage={!!hoverAsset}
            $noRotate={(brand == 'presonus' || isCustomHoverImage || hoverUrl?.includes("nr.png"))}
          />
        )}
        {(activeVariantIndex > 0) && (
          <HoverImage
            $isVariantHoverImage
            $isCustomHoverImage={!!hoverAsset}
            src={swatches[activeVariantIndex]?.variantImageUrl}
            $noRotate={(brand == 'presonus' || swatches[activeVariantIndex]?.variantImageUrl?.includes('nr.png'))}
          />
        )}
      </ImageContainer>
      <ContentContainer $brand={brand}>
        <TextBadgeWrapper>
          {textBadge?.value && (
            <Badge $brand={brand} $theme={theme} $badgeType="text">
              {textBadge.value}
            </Badge>
          )}
          {textBadgeTwo?.value && (
            <Badge $brand={brand} $theme={theme} $badgeType="textTwo">
              {textBadgeTwo.value}
            </Badge>
          )}
        </TextBadgeWrapper>
        <Title $brand={brand}>{title}</Title>
        {(!disablePricing && brand == 'jackson') && (
          <Price $brand={brand} $theme={theme} data-la-dn-price>{shopCurrencySymbol}{price}</Price>
        )}
        {(brand == 'jackson' && swatches.length > 1) && (
          <ProductTileSwatches
            activeVariantIndex={activeVariantIndex}
            brand={brand}
            layout={layout}
            variants={swatches}
            onSwatchHover={(index) => setActiveVariantIndex(index)}
          />
        )}
        {(!disablePricing && brand !== 'jackson') && (
          <Price $brand={brand} $theme={theme} data-la-dn-price>{shopCurrencySymbol}{price}</Price>
        )}
        {productBadge?.value && (
          <Badge $brand={brand} $theme={theme} $badgeType="product">
            {productBadge.value}
          </Badge>
        )}
        {calloutMessage?.value && (
          <Badge $brand={brand} $theme={theme} $badgeType="callout">
            {calloutMessage.value}
          </Badge>
        )}
      </ContentContainer>
    </Tile>
  );
};

export default ProductTile;
